import { BooleanInput, Button, Container, Element, Label, SelectInput, TextInput } from '@playcanvas/pcui';

import { parseVehicleMotionCsv } from '../csv-vehicle-parser';
import { Events } from '../events';
import type { VehicleMotionAxisPreset, VehicleMotionImportOptions } from '../vehicle-motion-import';
import { localize } from './localization';
import sceneExport from './svg/export.svg';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class VehicleMotionImportDialog extends Container {
    show: (args?: { text?: string; filename?: string }) => Promise<VehicleMotionImportOptions | null>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'vehicle-motion-import-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        // a11y
        this.dom.setAttribute('role', 'dialog');
        this.dom.setAttribute('aria-modal', 'true');

        const dialog = new Container({ id: 'dialog' });

        // header
        const headerIcon = createSvg(sceneExport, { id: 'icon' });
        const headerText = new Label({ id: 'text', text: localize('popup.vehicle-motion.header').toUpperCase() });
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // content
        const content = new Container({ id: 'content' });

        // Preview (computed from CSV text if provided)
        const preview = new Container({ id: 'vehicle-motion-preview' });
        const previewSummary = new Label({ text: '' });
        const previewAngles = new Label({ text: '' });
        const previewNote = new Label({ text: '' });
        // Keep it subtle / compact (no dedicated CSS yet).
        previewSummary.dom.style.fontSize = '11px';
        previewSummary.dom.style.opacity = '0.85';
        previewAngles.dom.style.fontSize = '11px';
        previewAngles.dom.style.opacity = '0.75';
        previewAngles.dom.style.marginTop = '2px';
        previewNote.dom.style.fontSize = '11px';
        previewNote.dom.style.opacity = '0.75';
        previewNote.dom.style.marginTop = '2px';
        preview.append(previewSummary);
        preview.append(previewAngles);
        preview.append(previewNote);

        // axis preset
        const axisLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.axis-preset') });
        const axisSelect = new SelectInput({
            id: 'vehicle-motion-axis-preset',
            class: 'select',
            defaultValue: 'zUpToYUp',
            options: [
                { v: 'zUpToYUp', t: localize('popup.vehicle-motion.axis-preset.zup') },
                { v: 'yUpKeep', t: localize('popup.vehicle-motion.axis-preset.yup') }
            ]
        });
        const axisRow = new Container({ class: 'row' });
        axisRow.append(axisLabel);
        axisRow.append(axisSelect);

        // fps override
        const fpsLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.fps') });
        const fpsInput = new TextInput({
            id: 'vehicle-motion-fps',
            class: 'text-input'
        });
        const fpsRow = new Container({ class: 'row' });
        fpsRow.append(fpsLabel);
        fpsRow.append(fpsInput);

        // extend timeline
        const extendLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.extend-timeline') });
        const extendBoolean = new BooleanInput({
            id: 'vehicle-motion-extend-timeline',
            class: 'boolean',
            value: true
        });
        const extendRow = new Container({ class: 'row' });
        extendRow.append(extendLabel);
        extendRow.append(extendBoolean);

        // yaw invert
        const invertYawLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.invert-yaw') });
        const invertYawBoolean = new BooleanInput({
            id: 'vehicle-motion-invert-yaw',
            class: 'boolean',
            value: false
        });
        const invertYawRow = new Container({ class: 'row' });
        invertYawRow.append(invertYawLabel);
        invertYawRow.append(invertYawBoolean);

        // pitch invert
        const invertPitchLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.invert-pitch') });
        const invertPitchBoolean = new BooleanInput({
            id: 'vehicle-motion-invert-pitch',
            class: 'boolean',
            value: false
        });
        const invertPitchRow = new Container({ class: 'row' });
        invertPitchRow.append(invertPitchLabel);
        invertPitchRow.append(invertPitchBoolean);

        // roll invert
        const invertRollLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.invert-roll') });
        const invertRollBoolean = new BooleanInput({
            id: 'vehicle-motion-invert-roll',
            class: 'boolean',
            value: false
        });
        const invertRollRow = new Container({ class: 'row' });
        invertRollRow.append(invertRollLabel);
        invertRollRow.append(invertRollBoolean);

        // swap pitch/roll
        const swapLabel = new Label({ class: 'label', text: localize('popup.vehicle-motion.swap-pitch-roll') });
        const swapBoolean = new BooleanInput({
            id: 'vehicle-motion-swap-pitch-roll',
            class: 'boolean',
            value: false
        });
        const swapRow = new Container({ class: 'row' });
        swapRow.append(swapLabel);
        swapRow.append(swapBoolean);

        // wheel spin axis
        const wheelSpinAxisLabel = new Label({ class: 'label', text: 'Wheel Spin Axis' });
        const wheelSpinAxisSelect = new SelectInput({
            id: 'vehicle-motion-wheel-spin-axis',
            class: 'select',
            defaultValue: 'x',
            options: [
                { v: 'x', t: 'X (default)' },
                { v: 'y', t: 'Y' },
                { v: 'z', t: 'Z' }
            ]
        });
        const wheelSpinAxisRow = new Container({ class: 'row' });
        wheelSpinAxisRow.append(wheelSpinAxisLabel);
        wheelSpinAxisRow.append(wheelSpinAxisSelect);

        // wheel spin units
        const wheelSpinUnitsLabel = new Label({ class: 'label', text: 'Wheel Spin Units' });
        const wheelSpinUnitsSelect = new SelectInput({
            id: 'vehicle-motion-wheel-spin-units',
            class: 'select',
            defaultValue: 'deg',
            options: [
                { v: 'deg', t: 'Degrees (default)' },
                { v: 'rad', t: 'Radians' }
            ]
        });
        const wheelSpinUnitsRow = new Container({ class: 'row' });
        wheelSpinUnitsRow.append(wheelSpinUnitsLabel);
        wheelSpinUnitsRow.append(wheelSpinUnitsSelect);

        // slip angle steering (advanced)
        const slipSteerLabel = new Label({ class: 'label', text: 'Use TYRE_SA as steer (advanced)' });
        const slipSteerBoolean = new BooleanInput({
            id: 'vehicle-motion-use-slip-angle-as-steer',
            class: 'boolean',
            value: false
        });
        const slipSteerRow = new Container({ class: 'row' });
        slipSteerRow.append(slipSteerLabel);
        slipSteerRow.append(slipSteerBoolean);

        content.append(axisRow);
        content.append(fpsRow);
        content.append(extendRow);
        content.append(invertYawRow);
        content.append(invertPitchRow);
        content.append(invertRollRow);
        content.append(swapRow);
        content.append(wheelSpinAxisRow);
        content.append(wheelSpinUnitsRow);
        content.append(slipSteerRow);

        // Put preview at the top (so the user sees it before tweaking options).
        content.dom.insertBefore(preview.dom, content.dom.firstChild);

        // footer
        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            id: 'vehicle-motion-import-cancel',
            class: 'button',
            text: localize('popup.cancel')
        });

        const importButton = new Button({
            id: 'vehicle-motion-import-ok',
            class: 'button',
            text: localize('popup.vehicle-motion.import')
        });

        footer.append(cancelButton);
        footer.append(importButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // handlers
        let onCancel: () => void;
        let onImport: () => void;

        cancelButton.on('click', () => onCancel());
        importButton.on('click', () => onImport());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    onCancel();
                    break;
                case 'Enter':
                    e.preventDefault();
                    e.stopPropagation();
                    onImport();
                    break;
                default:
                    // Prevent editor shortcuts / timeline keybinds from firing while modal is open.
                    e.stopPropagation();
                    break;
            }
        };

        const reset = () => {
            axisSelect.value = 'zUpToYUp';
            extendBoolean.value = true;
            invertYawBoolean.value = false;
            invertPitchBoolean.value = false;
            invertRollBoolean.value = false;
            swapBoolean.value = false;
            wheelSpinAxisSelect.value = 'x';
            wheelSpinUnitsSelect.value = 'deg';
            slipSteerBoolean.value = false;

            // Leave FPS blank by default so importer can use current timeline frameRate.
            fpsInput.value = '';

            const timelineFps = events.invoke('timeline.frameRate');
            const hint = (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) ? String(timelineFps) : '30';

            // Best-effort placeholder support (PCUI doesn't expose this consistently across versions).
            try {
                const inputEl = (fpsInput as any).input as HTMLInputElement | undefined;
                if (inputEl) {
                    inputEl.placeholder = `${localize('popup.vehicle-motion.fps.placeholder')} (${hint})`;
                }
            } catch (err) {
                // ignore
            }
        };

        // Preview state
        let csvText: string | null = null;
        let csvFilename: string | null = null;
        let parsedDurationSec = 0;
        let parsedSampleCount = 0;
        let parsedVehicleNo: number | null = null;
        let parsedWarnings: string[] = [];
        let parsedOk = false;

        const parsePreview = () => {
            parsedOk = false;
            parsedWarnings = [];
            parsedDurationSec = 0;
            parsedSampleCount = 0;
            parsedVehicleNo = null;

            if (!csvText) {
                return;
            }

            const parsed = parseVehicleMotionCsv(csvText, { zeroTimeToFirstSample: true });
            parsedWarnings = parsed.warnings ?? [];
            parsedSampleCount = parsed.samples?.length ?? 0;
            parsedVehicleNo = parsed.meta?.vehicleNo ?? null;

            if (parsedSampleCount > 0) {
                parsedDurationSec = parsed.samples[parsedSampleCount - 1].tSec;
                parsedOk = true;
            }
        };

        const getEffectiveFps = (): number => {
            const rawFps = String(fpsInput.value ?? '').trim();
            if (rawFps !== '') {
                const parsed = Number(rawFps);
                if (Number.isFinite(parsed) && parsed > 0) return parsed;
            }

            const timelineFps = events.invoke('timeline.frameRate');
            if (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) return timelineFps;
            return 30;
        };

        const getFramesRequired = (durationSec: number, fps: number) => {
            return Math.max(1, Math.ceil(durationSec * fps) + 1);
        };

        const updatePreview = () => {
            // Default: blank preview if no CSV text was provided.
            if (!csvText) {
                previewSummary.text = '';
                previewAngles.text = '';
                previewNote.text = '';
                importButton.disabled = false;
                return;
            }

            const fps = getEffectiveFps();
            const framesRequired = getFramesRequired(parsedDurationSec, fps);
            const vehicleLabel = (parsedVehicleNo !== null && Number.isFinite(parsedVehicleNo)) ? `v${parsedVehicleNo}` : 'v';

            const filePart = csvFilename ? `${csvFilename} — ` : '';

            if (!parsedOk) {
                importButton.disabled = true;

                const reason = parsedWarnings[0] ?? 'Could not parse CSV or no samples found.';
                previewSummary.text = `${filePart}${reason}`;
                previewAngles.text = '';
                previewNote.text = '';
                previewSummary.dom.style.color = '#f88';
                return;
            }

            importButton.disabled = false;
            previewSummary.dom.style.color = '';

            const warnCount = parsedWarnings.length;
            previewSummary.text = `${filePart}${vehicleLabel} • ${parsedSampleCount} samples • ${parsedDurationSec.toFixed(2)}s • ${framesRequired} frames @ ${fps}fps • ${warnCount} warnings`;

            // Clarify angle mapping so users can diagnose pitch/roll axis issues quickly.
            const axisPreset = axisSelect.value;
            const swaps = !!swapBoolean.value;
            const invYaw = !!invertYawBoolean.value;
            const invPitch = !!invertPitchBoolean.value;
            const invRoll = !!invertRollBoolean.value;
            previewAngles.text = `Angles: pitch→X, roll→Y, yaw→Z (${axisPreset}); swap pitch/roll: ${swaps ? 'on' : 'off'}; invert: pitch ${invPitch ? 'on' : 'off'}, roll ${invRoll ? 'on' : 'off'}, yaw ${invYaw ? 'on' : 'off'}`;

            const currentFrames = events.invoke('timeline.frames');
            const extend = !!extendBoolean.value;
            const currentFramesNum = (typeof currentFrames === 'number' && Number.isFinite(currentFrames)) ? currentFrames : null;

            if (extend && currentFramesNum !== null && framesRequired > currentFramesNum) {
                previewNote.text = `Timeline will extend to ${framesRequired} frames.`;
            } else if (!extend && currentFramesNum !== null && framesRequired > currentFramesNum) {
                previewNote.text = `Motion exceeds current timeline (${currentFramesNum} frames). Consider enabling “Extend timeline”.`;
            } else {
                previewNote.text = '';
            }
        };

        // Keep preview reactive to option changes.
        fpsInput.on('change', () => updatePreview());
        extendBoolean.on('change', () => updatePreview());
        axisSelect.on('change', () => updatePreview());
        invertYawBoolean.on('change', () => updatePreview());
        invertPitchBoolean.on('change', () => updatePreview());
        invertRollBoolean.on('change', () => updatePreview());
        swapBoolean.on('change', () => updatePreview());

        this.show = (args?: { text?: string; filename?: string }) => {
            reset();

            csvText = typeof args?.text === 'string' ? args.text : null;
            csvFilename = typeof args?.filename === 'string' ? args.filename : null;
            parsePreview();
            updatePreview();

            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            this.dom.focus();

            return new Promise<VehicleMotionImportOptions | null>((resolve) => {
                onCancel = () => resolve(null);

                onImport = async () => {
                    const axisPreset = axisSelect.value as VehicleMotionAxisPreset;

                    const rawFps = String(fpsInput.value ?? '').trim();
                    let fps: number | undefined;
                    if (rawFps !== '') {
                        const parsed = Number(rawFps);
                        if (!Number.isFinite(parsed) || parsed <= 0) {
                            await events.invoke('showPopup', {
                                type: 'error',
                                header: localize('popup.error'),
                                message: localize('popup.vehicle-motion.fps.invalid')
                            });
                            return;
                        }
                        fps = parsed;
                    }

                    const eulerOverrides = {
                        invertYaw: !!invertYawBoolean.value,
                        invertPitch: !!invertPitchBoolean.value,
                        invertRoll: !!invertRollBoolean.value,
                        swapPitchRoll: !!swapBoolean.value
                    };

                    const wheelSpinAxis = wheelSpinAxisSelect.value as VehicleMotionImportOptions['wheelSpinAxis'];
                    const wheelSpinUnits = wheelSpinUnitsSelect.value as VehicleMotionImportOptions['wheelSpinUnits'];
                    const useSlipAngleAsSteer = !!slipSteerBoolean.value;

                    resolve({
                        axisPreset,
                        eulerOverrides,
                        fps,
                        extendTimeline: !!extendBoolean.value,
                        wheelSpinAxis,
                        wheelSpinUnits,
                        useSlipAngleAsSteer
                    });
                };
            }).finally(() => {
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { VehicleMotionImportDialog };
